'use client'

import Link from 'next/link'
import { useState } from 'react'

import LegalBackButton from '../LegalBackButton'

type Locale = 'pt' | 'en'

const updatedAt = 'May 11, 2026'

const copy = {
  en: {
    brand: '7Songs / 7Corp',
    title: 'Terms of Service',
    updated: 'Last updated',
    sections: [
      {
        title: 'Use of YouTube API Services',
        paragraphs: [
          <>
            By using 7Songs features that access YouTube, including connecting a YouTube account,
            viewing YouTube playlists, searching YouTube videos, creating playlists, or adding videos
            to playlists, you agree to be bound by the{' '}
            <a className="text-cyan-300 underline" href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
              YouTube Terms of Service
            </a>.
          </>,
          'You also agree that these YouTube-related features are provided through YouTube API Services and must be used only in accordance with applicable YouTube API Services Terms, Developer Policies, and Google policies.',
        ],
      },
      {
        title: 'Account Authorization',
        paragraphs: [
          'YouTube account access is authorized by you through Google OAuth. You may revoke 7Songs access at any time in your Google account security settings.',
          '7Songs does not own your YouTube channel, videos, playlists, or Google account. Actions such as creating playlists or adding videos occur only after an authenticated user action inside the product.',
        ],
      },
      {
        title: 'Contact',
        paragraphs: [
          <>
            Organization: 7Corp - 7SETE LTDA. Contact:{' '}
            <a className="text-cyan-300 underline" href="mailto:renan@7sete.com">renan@7sete.com</a>.
          </>,
        ],
      },
    ],
    privacy: 'Privacy Policy',
    contact: 'Contact',
  },
  pt: {
    brand: '7Songs / 7Corp',
    title: 'Termos de Servico',
    updated: 'Ultima atualizacao',
    sections: [
      {
        title: 'Uso dos YouTube API Services',
        paragraphs: [
          <>
            Ao usar recursos da 7Songs que acessam o YouTube, incluindo conectar uma conta YouTube,
            visualizar playlists, pesquisar videos, criar playlists ou adicionar videos a playlists,
            voce concorda em ficar vinculado aos{' '}
            <a className="text-cyan-300 underline" href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
              Termos de Servico do YouTube
            </a>.
          </>,
          'Voce tambem concorda que esses recursos relacionados ao YouTube sao fornecidos por meio dos YouTube API Services e devem ser usados de acordo com os termos, politicas de desenvolvedor e politicas do Google aplicaveis.',
        ],
      },
      {
        title: 'Autorizacao da Conta',
        paragraphs: [
          'O acesso a conta YouTube e autorizado por voce via Google OAuth. Voce pode revogar o acesso da 7Songs a qualquer momento nas configuracoes de seguranca da sua conta Google.',
          'A 7Songs nao e proprietaria do seu canal, videos, playlists ou conta Google. Acoes como criar playlists ou adicionar videos ocorrem somente apos uma acao autenticada do usuario dentro do produto.',
        ],
      },
      {
        title: 'Contato',
        paragraphs: [
          <>
            Organizacao: 7Corp - 7SETE LTDA. Contato:{' '}
            <a className="text-cyan-300 underline" href="mailto:renan@7sete.com">renan@7sete.com</a>.
          </>,
        ],
      },
    ],
    privacy: 'Politica de Privacidade',
    contact: 'Contato',
  },
} satisfies Record<Locale, {
  brand: string
  title: string
  updated: string
  sections: Array<{ title: string; paragraphs: React.ReactNode[] }>
  privacy: string
  contact: string
}>

export default function TermsPage() {
  const [locale, setLocale] = useState<Locale>('en')
  const page = copy[locale]

  return (
    <main className="min-h-screen bg-[#09080b] text-white px-6 py-12">
      <div className="mx-auto max-w-3xl space-y-8">
        <header className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <LegalBackButton />
            <div className="flex items-center gap-1 rounded-lg border border-white/15 bg-black/20 p-1">
              {(['pt', 'en'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setLocale(option)}
                  className={`rounded px-3 py-1.5 text-xs font-bold transition-colors ${
                    locale === option
                      ? 'bg-cyan-500/25 text-cyan-200'
                      : 'text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {option.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <p className="text-sm font-semibold text-cyan-300">{page.brand}</p>
          <h1 className="text-3xl font-black">{page.title}</h1>
          <p className="text-sm text-neutral-400">{page.updated}: {updatedAt}</p>
        </header>

        {page.sections.map((section) => (
          <section key={section.title} className="space-y-3 text-sm leading-6 text-neutral-300">
            <h2 className="text-xl font-bold text-white">{section.title}</h2>
            {section.paragraphs.map((paragraph, index) => (
              <p key={`${section.title}-${index}`}>{paragraph}</p>
            ))}
          </section>
        ))}

        <nav className="flex flex-wrap gap-3 pt-4 text-sm">
          <Link className="text-cyan-300 underline" href="/privacy">{page.privacy}</Link>
          <Link className="text-cyan-300 underline" href="/contact">{page.contact}</Link>
        </nav>
      </div>
    </main>
  )
}
